import {
  ImageCoverView,
  ImageScaleView,
  ImageScaleViewOpts,
  View,
} from '@play-co/timestep-core/ui';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import {
  Match2Reward,
  isTimedReward,
} from 'src/replicant/match2/rewards/match2-rewards.ruleset';
import {
  animateRewards,
  getMatch2RewardAsset,
  getMatch2RewardDecoration,
  getMatch2RewardValue,
} from '../rewards/match2-rewards.utils';
import i18n from 'src/lib/i18n/i18n';
import { SuperLollipopIcon } from 'src/game/superLollipop/components/SuperLollipopIcon';

type Match2RewardItemOpts = Omit<
  ImageScaleViewOpts,
  'image' | 'width' | 'height'
> & {
  size?: number;
  itemPadding?: number;
  labelOffsetX?: number;
  labelOffsetY?: number;
  labelSize?: number;
  prefix?: string;
};

export class Match2RewardItem extends View {
  private rewardIcon: ImageCoverView;
  private rewardBanner: ImageScaleView;
  private countText: LangBitmapFontTextView;
  private multiplierText: LangBitmapFontTextView;
  private reward!: Match2Reward;
  private labelOffsetX: number;
  private labelOffsetY: number;
  private labelSize: number;
  private prefix: string;

  constructor(opts: Match2RewardItemOpts = {}) {
    const width = opts.size ?? 110;
    const height = opts.size ?? 110;
    const padding = opts.itemPadding ?? 0;

    super({
      width: width + padding,
      height: height + padding,
      ...opts,
    });

    this.labelOffsetX = opts.labelOffsetX ?? 0;
    this.labelOffsetY = opts.labelOffsetY ?? 0;
    this.labelSize = opts.labelSize ?? 35;
    this.prefix = opts.prefix ?? '';

    this.rewardIcon = new ImageCoverView({
      parent: this,
      width,
      height,
      mode: 'contain',
    });

    this.rewardBanner = new ImageScaleView({
      superview: this,
      width: width,
      height: height,
      centerOnOrigin: true,
      centerAnchor: true,
      scaleMethod: 'contain',
      x: width * 0.5,
      y: height - 10,
      scale: 1.2,
    });

    this.countText = new LangBitmapFontTextView({
      parent: this,
      font: 'Title',
      color: '#FFFFFF',
      size: this.labelSize,
      width: width - 10,
      height: 30,
      x: width * 0.5 + this.labelOffsetX,
      y: height - 20 + this.labelOffsetY,
      centerOnOrigin: true,
      verticalAlign: 'center',
      align: 'center',
    });

    this.multiplierText = new LangBitmapFontTextView({
      parent: this,
      font: 'Title',
      color: '#FFFFFF',
      size: Math.floor(this.labelSize * 1.15),
      x: width - 30,
      y: 30,
      centerOnOrigin: true,
      verticalAlign: 'center',
      align: 'center',
    });
  }

  public setReward(reward?: Match2Reward) {
    if (!reward) {
      this.hide();
      return;
    }

    this.show();
    const multiplier = reward.type === 'multiplier';
    const timed = isTimedReward(reward);
    const useBanner = multiplier || timed;
    this.reward = reward;
    getMatch2RewardAsset(reward).then((image) =>
      this.rewardIcon.setImage(image),
    );
    this.rewardBanner.setImage(
      `assets/ui/match2/frenzy/common/badge_frenzy_${
        timed ? 'unlimited' : 'timer'
      }.png`,
    );
    this.rewardBanner.style.visible = useBanner;
    this.multiplierText.style.visible = multiplier;
    if (multiplier) this.multiplierText.localeText = () => `x${reward.amount}`;
    this.countText.updateOpts({
      size: useBanner ? Math.floor(this.labelSize * 0.7) : this.labelSize,
      font: 'Title',
      x: this.style.width * 0.5 + (useBanner ? 6 : 0) + this.labelOffsetX,
      y:
        this.style.height -
        (multiplier ? 18 : useBanner ? 14 : 20) +
        this.labelOffsetY,
    });
    const decoration = getMatch2RewardDecoration(reward);
    this.countText.style.visible =
      decoration?.type !== 'chest' &&
      reward.type !== 'roomDecoration' &&
      reward.type !== 'card';
    if (this.countText.style.visible) {
      this.countText.localeText = () => {
        const value = getMatch2RewardValue(reward);
        return `${useBanner ? '' : this.prefix}${value}`;
      };
    }

    // Custom formatting for Super Lollipop
    if (reward.type === 'feature' && reward.subType === 'superLollipop') {
      new SuperLollipopIcon({
        superview: this.rewardIcon,
        width: this.rewardIcon.style.width,
        height: this.rewardIcon.style.height,
        x: this.rewardIcon.style.width * 0.5,
        y: this.rewardIcon.style.height * 0.5,
        scale: this.rewardIcon.style.width / 65,
      });
      this.countText.localeText = () => i18n('superLollipop.shortName');
      this.countText.updateOpts({
        width: this.style.width + 60,
        centerOnOrigin: true,
      });
    }
  }

  public async grantReward(superview: View) {
    const position = this.rewardIcon.getPosition(superview);
    position.x += position.width / 2;
    position.y += position.height / 2;
    await animateRewards(superview, this.reward, position);
  }

  public getReward() {
    return this.reward;
  }
}
